import React, { useState, useEffect, useRef } from 'react';
import { Typography, Spin, Flex, theme, Collapse } from 'antd';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkBreaks from 'remark-breaks';
import { useTranslation } from '../i18n/i18n';
import PageLayout from '../components/PageLayout';

const { Title } = Typography;
const { Panel } = Collapse;

interface FAQItem {
  id: string;
  question: string;
  answer: string;
}

const slugify = (text: string): string => {
  return text
    .toLowerCase()
    .replace(/[^\w\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .trim();
};

const parseFAQMarkdown = (markdown: string): { title: string; items: FAQItem[] } => {
  const lines = markdown.split('\n');
  let title = 'Frequently Asked Questions';
  const items: FAQItem[] = [];
  let currentQuestion = '';
  let currentAnswer: string[] = [];
  let pastTitle = false;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();

    if (line.startsWith('## ')) {
      title = line.substring(2).trim();
      pastTitle = true;
      continue;
    }

    if (!pastTitle) {
      continue;
    }

    if (line === '---') {
      continue;
    }

    if (line.startsWith('### ')) {
      if (currentQuestion && currentAnswer.length > 0) {
        items.push({
          id: slugify(currentQuestion),
          question: currentQuestion,
          answer: currentAnswer.join('\n').trim(),
        });
      }
      currentQuestion = line.substring(3).trim();
      currentAnswer = [];
    } else if (currentQuestion) {
      currentAnswer.push(lines[i]);
    }
  }

  if (currentQuestion && currentAnswer.length > 0) {
    items.push({
      id: slugify(currentQuestion),
      question: currentQuestion,
      answer: currentAnswer.join('\n').trim(),
    });
  }

  return { title, items };
};

const FAQ: React.FC = () => {
  const { language } = useTranslation();
  const { token } = theme.useToken();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [faqData, setFaqData] = useState<{ title: string; items: FAQItem[] } | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [activeKeys, setActiveKeys] = useState<string[]>([]);
  const questionRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

  const questionId = searchParams.get('question');

  useEffect(() => {
    const loadFAQ = async () => {
      try {
        const fileName = language === 'pt-BR' ? '/FAQ.pt-BR.md' : '/FAQ.en.md';
        const response = await fetch(fileName);
        if (!response.ok) {
          throw new Error('Failed to load FAQ');
        }
        const text = await response.text();
        const parsed = parseFAQMarkdown(text);
        setFaqData(parsed);
      } catch (error) {
        console.error('Error loading FAQ:', error);
        const errorTitle = language === 'pt-BR' ? 'FAQ' : 'FAQ';
        const errorQuestion = language === 'pt-BR' ? 'Falha ao carregar FAQ' : 'Failed to load FAQ';
        const errorAnswer =
          language === 'pt-BR' ? 'Entre em contato com o suporte.' : 'Please contact support.';
        setFaqData({
          title: errorTitle,
          items: [
            {
              id: 'error',
              question: errorQuestion,
              answer: errorAnswer,
            },
          ],
        });
      } finally {
        setLoading(false);
      }
    };

    loadFAQ();
  }, [language]);

  useEffect(() => {
    if (questionId && faqData) {
      const item = faqData.items.find(item => item.id === questionId);
      if (item) {
        setActiveKeys([item.id]);
        setTimeout(() => {
          const element = questionRefs.current[item.id];
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 100);
      }
    }
  }, [questionId, faqData]);

  return (
    <PageLayout>
      <Flex vertical align="center" style={{ maxWidth: 900, margin: '0 auto' }}>
        {loading ? (
          <Flex justify="center" align="center" style={{ minHeight: '50vh' }}>
            <Spin size="large" />
          </Flex>
        ) : (
          <div
            style={{
              background: token.colorBgContainer,
              padding: token.paddingLG,
              borderRadius: token.borderRadius,
              border: `1px solid ${token.colorBorderSecondary}`,
              width: '100%',
            }}
          >
            {faqData && (
              <>
                <Title level={2} style={{ marginBottom: token.marginLG }}>
                  {faqData.title}
                </Title>
                <Collapse
                  activeKey={activeKeys}
                  onChange={keys => setActiveKeys(Array.isArray(keys) ? keys : [keys])}
                  style={{ background: 'transparent' }}
                >
                  {faqData.items.map(item => (
                    <Panel
                      header={<strong>{item.question}</strong>}
                      key={item.id}
                      style={{ marginBottom: token.marginXS }}
                    >
                      <div ref={el => (questionRefs.current[item.id] = el)}>
                        <ReactMarkdown
                          remarkPlugins={[remarkBreaks]}
                          components={{
                            p: ({ node, ...props }) => (
                              <p style={{ marginBottom: token.marginMD }} {...props} />
                            ),
                            br: () => <br />,
                            ul: ({ node, ...props }) => (
                              <ul
                                style={{
                                  marginBottom: token.marginMD,
                                  paddingLeft: token.marginLG,
                                }}
                                {...props}
                              />
                            ),
                            ol: ({ node, ...props }) => (
                              <ol
                                style={{
                                  marginBottom: token.marginMD,
                                  paddingLeft: token.marginLG,
                                }}
                                {...props}
                              />
                            ),
                            li: ({ node, ...props }) => (
                              <li style={{ marginBottom: token.marginXS }} {...props} />
                            ),
                            strong: ({ node, ...props }) => <strong {...props} />,
                            code: ({ node, inline, ...props }: any) =>
                              inline ? (
                                <code
                                  style={{
                                    background: token.colorFillSecondary,
                                    padding: `${token.paddingXXS}px ${token.paddingXS}px`,
                                    borderRadius: token.borderRadiusSM,
                                  }}
                                  {...props}
                                />
                              ) : (
                                <pre
                                  style={{
                                    background: token.colorFillSecondary,
                                    padding: token.paddingSM,
                                    borderRadius: token.borderRadiusSM,
                                    overflow: 'auto',
                                    marginBottom: token.marginMD,
                                  }}
                                  {...props}
                                />
                              ),
                            a: ({ node, href, children, ...props }: any) => {
                              const linkText = typeof children === 'string' ? children : '';
                              const hrefLower = href?.toLowerCase() || '';

                              if (
                                hrefLower.includes('privacy') ||
                                linkText.toLowerCase().includes('privacy policy')
                              ) {
                                return (
                                  <a
                                    href="/privacy"
                                    onClick={e => {
                                      e.preventDefault();
                                      navigate('/privacy');
                                    }}
                                    style={{ color: token.colorPrimary }}
                                    {...props}
                                  >
                                    {children}
                                  </a>
                                );
                              }

                              if (
                                hrefLower.includes('terms') ||
                                linkText.toLowerCase().includes('terms of service')
                              ) {
                                return (
                                  <a
                                    href="/terms"
                                    onClick={e => {
                                      e.preventDefault();
                                      navigate('/terms');
                                    }}
                                    style={{ color: token.colorPrimary }}
                                    {...props}
                                  >
                                    {children}
                                  </a>
                                );
                              }

                              if (href?.startsWith('#')) {
                                const targetId = href.substring(1);
                                return (
                                  <a
                                    href={`/faq?question=${targetId}`}
                                    onClick={e => {
                                      e.preventDefault();
                                      setSearchParams({ question: targetId });
                                      const item = faqData.items.find(i => i.id === targetId);
                                      if (item) {
                                        setActiveKeys([item.id]);
                                        setTimeout(() => {
                                          const element = questionRefs.current[item.id];
                                          if (element) {
                                            element.scrollIntoView({
                                              behavior: 'smooth',
                                              block: 'start',
                                            });
                                          }
                                        }, 100);
                                      }
                                    }}
                                    style={{ color: token.colorPrimary }}
                                    {...props}
                                  >
                                    {children}
                                  </a>
                                );
                              }

                              if (href === '/terms' || href === 'terms') {
                                return (
                                  <a
                                    href="/terms"
                                    onClick={e => {
                                      e.preventDefault();
                                      navigate('/terms');
                                    }}
                                    style={{ color: token.colorPrimary }}
                                    {...props}
                                  >
                                    {children}
                                  </a>
                                );
                              }

                              if (href === '/privacy' || href === 'privacy') {
                                return (
                                  <a
                                    href="/privacy"
                                    onClick={e => {
                                      e.preventDefault();
                                      navigate('/privacy');
                                    }}
                                    style={{ color: token.colorPrimary }}
                                    {...props}
                                  >
                                    {children}
                                  </a>
                                );
                              }

                              return (
                                <a href={href} {...props}>
                                  {children}
                                </a>
                              );
                            },
                          }}
                        >
                          {item.answer}
                        </ReactMarkdown>
                      </div>
                    </Panel>
                  ))}
                </Collapse>
              </>
            )}
          </div>
        )}
      </Flex>
    </PageLayout>
  );
};

export default FAQ;
